import { FolderCodeIcon, InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, Navigate, NavLink, Route, Routes, useParams } from 'react-router';
import { useTitle } from 'react-use';

import {
  PageSubHeader,
  PageSubHeaderBreadcrumbs,
  PageSubHeaderTabs,
} from '@/components/organisms/PageSubHeader';
import Layout from '@/components/templates/Layout';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { selectAccountSlug } from '@/redux/session/selectors';

import { DEVELOPERS_SETTINGS_PATHS } from './constants';

export const DeveloperToolsRouter = () => {
  const { t } = useTranslation();
  const { '*': splat = '' } = useParams();
  const slug = useSelector(selectAccountSlug);
  useTitle(t('developer_tools.title'));

  const tabs = [
    {
      path: 'apikeys',
      label: t('developer_tools.api_keys'),
      settingsPath: DEVELOPERS_SETTINGS_PATHS.apiKeys,
    },
    {
      path: 'models',
      label: t('developer_tools.llms'),
      settingsPath: DEVELOPERS_SETTINGS_PATHS.llms,
    },
    {
      path: 'plugins',
      label: t('developer_tools.plugins'),
      settingsPath: DEVELOPERS_SETTINGS_PATHS.eventNotifications,
    },
  ];

  const currentTab = tabs.find((tab) => tab.path === splat.split('/')[0]) ?? tabs[0];

  return (
    <Layout>
      <PageSubHeader
        inlineTabs
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    <div className="flex h-8 w-8 items-center justify-center">
                      <FolderCodeIcon size={16} className="text-foreground-muted" />
                    </div>
                    <div className="mr-4 ml-2 h-4 w-px bg-border" />
                    <span className="text-sm text-foreground">{t('developer_tools.title')}</span>
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </PageSubHeaderBreadcrumbs>
        }
        tabs={
          <PageSubHeaderTabs>
            <Tabs value={currentTab.path} className="w-auto">
              <TabsList>
                {tabs.map((tab) => (
                  <TabsTrigger key={tab.path} value={tab.path} asChild className="h-8">
                    <NavLink to={`/${slug}/devtools/${tab.path}`}>{tab.label}</NavLink>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </PageSubHeaderTabs>
        }
      />

      <Routes>
        <Route index element={<Navigate to={tabs[0].path} replace />} />
        <Route
          path="*"
          element={
            <Alert
              variant="info"
              role="status"
              className="flex items-center justify-center rounded-none p-2 has-[>svg]:gap-x-2 [&>svg]:translate-y-0"
            >
              <InfoIcon size={16} className="shrink-0" />
              <AlertDescription className="flex flex-wrap items-center gap-x-2 leading-5">
                {t('developer_tools.moved_to_settings')}
                <Link
                  to={`/${slug}${currentTab.settingsPath}`}
                  className="font-medium underline underline-offset-2"
                >
                  {t('developer_tools.open_in_settings')}
                </Link>
              </AlertDescription>
            </Alert>
          }
        />
      </Routes>
    </Layout>
  );
};
